import { TILE } from '@/gfx/tiles';
import { idx, type Floor, type DoorEnt } from '@/world/builder';
import { T_FLOOR, T_DOOR, T_STAIRS } from '@/world/types';

/** Half-size of the feet collision box. */
export const HW = 4;
export const HH = 3;

export interface Mover {
  x: number;
  y: number;
}

/** Is the tile passable for a mover? Doors: passable when open (or when `openDoor` returns true and opens it). */
export function tilePassable(f: Floor, tx: number, ty: number, doorRule: (d: DoorEnt) => boolean): boolean {
  if (tx < 0 || ty < 0 || tx >= f.w || ty >= f.h) return false;
  const i = idx(f, tx, ty);
  const t = f.tiles[i];
  if (t === T_DOOR) {
    const d = f.doorAt.get(i)!;
    return d.open || doorRule(d);
  }
  if (t !== T_FLOOR && t !== T_STAIRS) return false;
  return !f.solid[i];
}

export function boxFree(f: Floor, x: number, y: number, doorRule: (d: DoorEnt) => boolean, hw = HW, hh = HH): boolean {
  const x0 = Math.floor((x - hw) / TILE), x1 = Math.floor((x + hw - 1) / TILE);
  const y0 = Math.floor((y - hh) / TILE), y1 = Math.floor((y + hh - 1) / TILE);
  for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) if (!tilePassable(f, tx, ty, doorRule)) return false;
  return true;
}

/** Axis-separated move with sliding. Returns true if blocked on any axis. */
export function moveBox(f: Floor, m: Mover, dx: number, dy: number, doorRule: (d: DoorEnt) => boolean, hw = HW, hh = HH): boolean {
  let blocked = false;
  if (dx !== 0) {
    if (boxFree(f, m.x + dx, m.y, doorRule, hw, hh)) m.x += dx;
    else {
      // step as far as possible
      const s = Math.sign(dx);
      let moved = false;
      for (let k = Math.abs(dx); k > 0.25; k /= 2) {
        if (boxFree(f, m.x + s * k, m.y, doorRule, hw, hh)) { m.x += s * k; moved = true; break; }
      }
      if (!moved) blocked = true;
    }
  }
  if (dy !== 0) {
    if (boxFree(f, m.x, m.y + dy, doorRule, hw, hh)) m.y += dy;
    else {
      const s = Math.sign(dy);
      let moved = false;
      for (let k = Math.abs(dy); k > 0.25; k /= 2) {
        if (boxFree(f, m.x, m.y + s * k, doorRule, hw, hh)) { m.y += s * k; moved = true; break; }
      }
      if (!moved) blocked = true;
    }
  }
  return blocked;
}

export function tileOf(v: number): number {
  return Math.floor(v / TILE);
}
export function tileCenter(t: number): number {
  return t * TILE + TILE / 2;
}

/** If a mover is stuck inside something solid (pushed furniture, bad spawn), move it to the nearest free tile centre. */
export function unstick(f: Floor, m: Mover): void {
  if (boxFree(f, m.x, m.y, (d) => d.open)) return;
  const tx = tileOf(m.x), ty = tileOf(m.y);
  for (let r = 0; r <= 4; r++) {
    for (let dy = -r; dy <= r; dy++)
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = tileCenter(tx + dx), y = tileCenter(ty + dy);
        if (boxFree(f, x, y, (d) => d.open)) { m.x = x; m.y = y; return; }
      }
  }
}
